"use client";

import React, { useState, useEffect } from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiArrowLeft, PiArrowRight, PiQuotes } from "react-icons/pi";

interface TestimonialItem {
    name: string;
    role: string;
    text: string;
    avatar: string;
}

export default function EducaeetTestimonials({ content, onContentChange, isSelected }: SectionContentProps) {
    const primaryBg = content.colorPalette?.primary || "#074d3d";
    const accentYellow = content.colorPalette?.accent || "#ffbd14";
    const fontFamily = content.fontFamily || "'Inter', 'Plus Jakarta Sans', sans-serif";

    const [currentIndex, setCurrentIndex] = useState(0);
    const [direction, setDirection] = useState<"next" | "prev">("next");
    const [isAnimating, setIsAnimating] = useState(false);

    const testimonials: TestimonialItem[] = content.testimonials || [
        {
            name: "Wanda Shawvern",
            role: "Software Engineering Student",
            text: "Instacks actually got me hired. I went from barely knowing what an API was to landing my first dev role in eight months. The projects feel real, the feedback is fast, and nothing felt like busywork.",
            avatar: "https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&q=80&w=800"
        },
        {
            name: "Sarah Johnson",
            role: "Web Development Student",
            text: "I tried two other platforms before this one. The difference is that Instacks actually makes you build things. I have a portfolio now, not just a certificate.",
            avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=800"
        },
        {
            name: "Christopher Story",
            role: "UI/UX Design Student",
            text: "The instruction is solid and the projects map directly to what clients actually want. I switched careers in under a year. No fluff.",
            avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?auto=format&fit=crop&q=80&w=800"
        }
    ];

    const current = testimonials[currentIndex] || testimonials[0];

    const triggerSlide = (newDir: "next" | "prev", nextIdx: number) => {
        if (isAnimating) return;
        setDirection(newDir);
        setIsAnimating(true);
        setCurrentIndex(nextIdx);
        setTimeout(() => setIsAnimating(false), 600);
    };

    const handleNext = () => {
        const nextIdx = (currentIndex + 1) % testimonials.length;
        triggerSlide("next", nextIdx);
    };

    const handlePrev = () => {
        const nextIdx = (currentIndex - 1 + testimonials.length) % testimonials.length;
        triggerSlide("prev", nextIdx);
    };

    useEffect(() => {
        const timer = setInterval(() => {
            handleNext();
        }, 5000);
        return () => clearInterval(timer);
    }, [currentIndex, testimonials.length]);

    return (
        <div
            className={`relative py-10 md:py-16 overflow-hidden ${isSelected ? "ring-2 ring-blue-500" : ""}`}
            style={{ backgroundColor: "#fafbfc", fontFamily }}
        >
            <style>{`
                @keyframes slideNext {
                    0% { opacity: 0; transform: translateX(60px); }
                    100% { opacity: 1; transform: translateX(0); }
                }
                @keyframes slidePrev {
                    0% { opacity: 0; transform: translateX(-60px); }
                    100% { opacity: 1; transform: translateX(0); }
                }
                .animate-slide-next {
                    animation: slideNext 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
                .animate-slide-prev {
                    animation: slidePrev 0.55s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>

            <div className="absolute top-4 left-4 opacity-20 pointer-events-none z-0 hidden md:block">
                <svg width="200" height="200" viewBox="0 0 200 200" fill="none">
                    <g fill="#074d3d">
                        <circle cx="20" cy="20" r="3.5" /> <circle cx="45" cy="20" r="3.5" /> <circle cx="70" cy="20" r="3.5" /> <circle cx="95" cy="20" r="3.5" />
                        <circle cx="20" cy="45" r="3.5" /> <circle cx="45" cy="45" r="3.5" /> <circle cx="70" cy="45" r="3.5" />
                        <circle cx="20" cy="70" r="3.5" /> <circle cx="45" cy="70" r="3.5" />
                        <circle cx="20" cy="95" r="3.5" />
                    </g>
                </svg>
            </div>

            <div className="container mx-auto px-6 max-w-6xl relative z-10">

                <div className="text-center mb-12">
                    <h2 className="text-3xl sm:text-4xl lg:text-[40px] font-bold text-gray-900 leading-tight tracking-tight max-w-3xl mx-auto">
                        What Our Learners{" "}
                        <span className="relative inline-block z-10 mx-1">
                            <span
                                className="absolute inset-0 top-[55%] bottom-[10%] -left-1 -right-1 -z-10 rounded-sm transform -rotate-1"
                                style={{ backgroundColor: accentYellow, opacity: 0.85 }}
                            />
                            Say About
                        </span>{" "}
                        Learning with Instacks
                    </h2>
                </div>

                <div className="relative max-w-4xl mx-auto flex items-center justify-center">

                    <div className="absolute left-[-15px] sm:left-[-25px] w-[95%] h-[90%] bg-white border border-gray-200/70 rounded-[32px] shadow-sm -z-10 transform -rotate-1 opacity-70 hidden sm:block" />

                    <div className="absolute right-[-15px] sm:right-[-25px] w-[95%] h-[90%] bg-white border border-gray-200/70 rounded-[32px] shadow-sm -z-10 transform rotate-1 opacity-70 hidden sm:block" />

                    <button
                        onClick={handlePrev}
                        className="absolute -left-4 sm:-left-6 z-30 w-11 h-11 rounded-full bg-white border border-[#0d9488] text-[#0d9488] flex items-center justify-center shadow-md transition-all hover:scale-110 hover:bg-emerald-50 cursor-pointer"
                        title="Previous Testimonial"
                    >
                        <PiArrowLeft className="w-5 h-5 stroke-[1.5]" />
                    </button>

                    <button
                        onClick={handleNext}
                        className="absolute -right-4 sm:-right-6 z-30 w-11 h-11 rounded-full bg-white border border-[#0d9488] text-[#0d9488] flex items-center justify-center shadow-md transition-all hover:scale-110 hover:bg-emerald-50 cursor-pointer"
                        title="Next Testimonial"
                    >
                        <PiArrowRight className="w-5 h-5 stroke-[1.5]" />
                    </button>

                    <div className="w-full bg-white border border-gray-200/90 rounded-[32px] p-6 sm:p-8 lg:p-10 shadow-xl relative z-20 overflow-hidden">
                        <div key={currentIndex} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center ${direction === "next" ? "animate-slide-next" : "animate-slide-prev"}`}>

                            <div className="lg:col-span-5 relative w-full h-[320px] sm:h-[360px] rounded-2xl overflow-hidden shadow-md">
                                <img
                                    src={current.avatar}
                                    alt={current.name}
                                    className="w-full h-full object-cover object-center"
                                />
                            </div>

                            <div className="lg:col-span-7 flex flex-col justify-between text-left">
                                <div>
                                    <div className="text-gray-400 mb-4">
                                        <PiQuotes className="w-10 h-10 rotate-180" />
                                    </div>

                                    <p className="text-gray-800 text-base sm:text-lg font-medium leading-relaxed mb-8">
                                        <EditableText
                                            value={current.text}
                                            onChange={(v) => {
                                                const newTestimonials = [...testimonials];
                                                newTestimonials[currentIndex] = { ...newTestimonials[currentIndex], text: v };
                                                onContentChange?.({ ...content, testimonials: newTestimonials });
                                            }}
                                        />
                                    </p>
                                </div>

                                <div className="pt-4 border-t border-gray-100">
                                    <h4 className="text-xl font-bold text-gray-900 tracking-tight">
                                        <EditableText
                                            value={current.name}
                                            onChange={(v) => {
                                                const newTestimonials = [...testimonials];
                                                newTestimonials[currentIndex] = { ...newTestimonials[currentIndex], name: v };
                                                onContentChange?.({ ...content, testimonials: newTestimonials });
                                            }}
                                        />
                                    </h4>
                                    <p className="text-xs sm:text-sm text-gray-400 font-medium mt-0.5">
                                        <EditableText
                                            value={current.role}
                                            onChange={(v) => {
                                                const newTestimonials = [...testimonials];
                                                newTestimonials[currentIndex] = { ...newTestimonials[currentIndex], role: v };
                                                onContentChange?.({ ...content, testimonials: newTestimonials });
                                            }}
                                        />
                                    </p>
                                </div>

                            </div>

                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
}
